import React, { useState, useEffect } from 'react';
import api from '../services/api';

const CARRIER_OPTIONS = [
  { value: 'att', label: 'AT&T' },
  { value: 'verizon', label: 'Verizon' },
  { value: 'tmobile', label: 'T-Mobile' },
  { value: 'sprint', label: 'Sprint' },
  { value: 'uscellular', label: 'US Cellular' },
  { value: 'cricket', label: 'Cricket' },
  { value: 'metropcs', label: 'Metro PCS' },
  { value: 'googlefi', label: 'Google Fi' },
  { value: 'boost', label: 'Boost Mobile' },
  { value: 'virgin', label: 'Virgin Mobile' },
];

const ALERT_TYPE_OPTIONS = [
  { value: 'green_light', label: 'Green Light Issues', desc: 'Vehicle problems on guaranteed cars' },
  { value: 'arb', label: 'ARB Alerts', desc: 'Arbitration deadlines' },
  { value: 'parts_arrived', label: 'Parts Arrived', desc: 'Parts room updates' },
  { value: 'vehicle_delivered', label: 'Vehicle Delivered', desc: 'New car arrived' },
  { value: 'title_alerts', label: 'Title Alerts', desc: 'Title status changes' },
  { value: 'payment_received', label: 'Payment Received', desc: 'Deal desk payments' },
  { value: 'all', label: 'All Alerts', desc: 'Receive all alert types' },
];

const AlertSubscriptions = ({ users }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    userId: '',
    name: '',
    email: '',
    phoneNumber: '',
    phoneCarrier: '',
    emailAlertsEnabled: true,
    textAlertsEnabled: false,
    alertTypes: [],
  });

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const loadSubscriptions = async () => {
    try {
      const data = await api.getAlertSubscriptions();
      setSubscriptions(data);
    } catch (err) {
      console.error('Failed to load subscriptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setForm({
      userId: '',
      name: '',
      email: '',
      phoneNumber: '',
      phoneCarrier: '',
      emailAlertsEnabled: true,
      textAlertsEnabled: false,
      alertTypes: [],
    });
    setEditingId(null);
    setShowModal(true);
  };

  const openEditModal = (sub) => {
    setForm({
      userId: sub.userId || '',
      name: sub.name || '',
      email: sub.email || '',
      phoneNumber: sub.phoneNumber || '',
      phoneCarrier: sub.phoneCarrier || '',
      emailAlertsEnabled: sub.emailAlertsEnabled,
      textAlertsEnabled: sub.textAlertsEnabled,
      alertTypes: sub.alertTypes || [],
    });
    setEditingId(sub.id);
    setShowModal(true);
  };

  const handleUserSelect = (userId) => {
    const user = users.find(u => u.id === parseInt(userId));
    if (user) {
      setForm(f => ({
        ...f,
        userId: user.id,
        name: user.name,
        email: user.email || '',
        phoneNumber: user.phone || '',
        phoneCarrier: user.phoneCarrier || '',
      }));
    } else {
      setForm(f => ({ ...f, userId: '' }));
    }
  };

  const toggleAlertType = (type) => {
    setForm(f => {
      const types = f.alertTypes.includes(type)
        ? f.alertTypes.filter(t => t !== type)
        : [...f.alertTypes, type];
      return { ...f, alertTypes: types };
    });
  };

  const handleSave = async () => {
    if (!form.name && !form.userId) {
      alert('Please select a user or enter a name');
      return;
    }
    if (!form.email && !form.phoneNumber) {
      alert('Please enter an email or phone number');
      return;
    }
    if (form.textAlertsEnabled && !form.phoneCarrier) {
      alert('Please select a carrier for text alerts');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await api.updateAlertSubscription(editingId, form);
      } else {
        await api.createAlertSubscription(form);
      }
      setShowModal(false);
      loadSubscriptions();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this alert subscription?')) return;
    try {
      await api.deleteAlertSubscription(id);
      loadSubscriptions();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    }
    return phone;
  };

  const formatAlertTypes = (types) => {
    if (!types || types.length === 0) return 'None';
    if (types.includes('all')) return 'All';
    return types.map(t => {
      const opt = ALERT_TYPE_OPTIONS.find(o => o.value === t);
      return opt ? opt.label : t;
    }).join(', ');
  };

  if (loading) {
    return <div className="p-8 text-center text-neutral-500">Loading subscriptions...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800">Alert Subscriptions</h1>
          <p className="text-neutral-500 text-sm">Manage text and email alerts for employees</p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium"
        >
          + Add Subscription
        </button>
      </div>

      {/* Subscriptions Table */}
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b">
            <tr className="text-left text-xs text-neutral-500 uppercase">
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Carrier</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Text</th>
              <th className="px-4 py-3">Alert Types</th>
              <th className="px-4 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {subscriptions.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-neutral-400">
                  No subscriptions yet. Click "+ Add Subscription" to create one.
                </td>
              </tr>
            ) : (
              subscriptions.map(sub => (
                <tr key={sub.id} className="hover:bg-neutral-50">
                  <td className="px-4 py-3">
                    <div className="font-medium">{sub.userName || sub.name}</div>
                    {sub.userRole && (
                      <div className="text-xs text-neutral-400 capitalize">{sub.userRole}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-sm">{formatPhone(sub.phoneNumber)}</td>
                  <td className="px-4 py-3 text-sm capitalize">{sub.phoneCarrier || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                      sub.emailAlertsEnabled ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'
                    }`}>
                      {sub.emailAlertsEnabled ? '✓' : '✗'} Email
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                      sub.textAlertsEnabled ? 'bg-blue-100 text-blue-700' : 'bg-neutral-100 text-neutral-500'
                    }`}>
                      {sub.textAlertsEnabled ? '✓' : '✗'} Text
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-600 max-w-xs truncate" title={formatAlertTypes(sub.alertTypes)}>
                    {formatAlertTypes(sub.alertTypes)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(sub)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(sub.id)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">How Text Alerts Work</h3>
        <p className="text-sm text-blue-700">
          Text alerts are sent via email-to-SMS gateways. Each carrier has a specific email format 
          (e.g., 5551234567@txt.att.net for AT&T). Make sure to select the correct carrier for each phone number.
        </p>
        <p className="text-sm text-blue-700 mt-2">
          Recipients can reply STOP to unsubscribe from text alerts. You can re-enable them here.
        </p>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg font-bold">{editingId ? 'Edit Subscription' : 'Add Subscription'}</h2>
              <button onClick={() => setShowModal(false)} className="text-neutral-400 hover:text-neutral-600 text-2xl">×</button>
            </div>

            <div className="p-6 space-y-4">
              {/* User Selection */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">User</label>
                <select
                  value={form.userId}
                  onChange={(e) => handleUserSelect(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                >
                  <option value="">-- Select User or Add New Contact --</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                  ))}
                </select>
              </div>

              {/* Name (for external contacts) */}
              {!form.userId && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                    placeholder="Contact name"
                  />
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                  placeholder="email@example.com"
                />
              </div>

              {/* Phone */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Phone Number</label>
                  <input
                    type="tel"
                    value={form.phoneNumber}
                    onChange={(e) => setForm(f => ({ ...f, phoneNumber: e.target.value }))}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                    placeholder="555-123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Carrier</label>
                  <select
                    value={form.phoneCarrier}
                    onChange={(e) => setForm(f => ({ ...f, phoneCarrier: e.target.value }))}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg"
                  >
                    <option value="">Select carrier...</option>
                    {CARRIER_OPTIONS.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Alert Toggles */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.emailAlertsEnabled}
                    onChange={(e) => setForm(f => ({ ...f, emailAlertsEnabled: e.target.checked }))}
                    className="w-5 h-5 rounded border-neutral-300"
                  />
                  <span>Email Alerts</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.textAlertsEnabled}
                    onChange={(e) => setForm(f => ({ ...f, textAlertsEnabled: e.target.checked }))}
                    className="w-5 h-5 rounded border-neutral-300"
                  />
                  <span>Text Alerts</span>
                </label>
              </div>

              {/* Alert Types */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Alert Types</label>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-neutral-200 rounded-lg p-3">
                  {ALERT_TYPE_OPTIONS.map(opt => (
                    <label key={opt.value} className="flex items-start gap-2 cursor-pointer hover:bg-neutral-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={form.alertTypes.includes(opt.value)}
                        onChange={() => toggleAlertType(opt.value)}
                        className="w-5 h-5 mt-0.5 rounded border-neutral-300"
                      />
                      <div>
                        <div className="font-medium text-sm">{opt.label}</div>
                        <div className="text-xs text-neutral-500">{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-neutral-50 border-t px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-neutral-600 hover:bg-neutral-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertSubscriptions;
